import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Button, Textarea } from 'rizzui';
import { DatePicker } from '../../../components/forms/DatePicker';
import { toast } from 'sonner';
import * as yup from 'yup';
import CustomSelect from '../../../components/forms/CustomSelect';
import RequiredInput from '../../../components/forms/InputHOC';
import ModalForm from '../../../components/forms/ModalForm';
import CustomModal from '../../../components/modal/CustomModal';
import { es } from 'date-fns/locale';
import moment from 'moment';
import useProducts from '../../../hooks/api-calls/useProducts';
import useUsers from '../../../hooks/api-calls/useUsers';
import useVehicles from '../../../hooks/api-calls/useVehicles';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { PickupService } from '../../../services/pickup/pickupService';
import { Pickup } from '../../../type/entities/pickup-type';
import { pickupStatuses } from '../../../utils/SelectorMapping';
import useCompanies from '../../../hooks/api-calls/useCompanies';
import useHandleErrors from '../../../hooks/useHandleErrors';

/**
 * UTILIDADES DE FORMATO DE FECHA
 * Funciones reutilizables para manejo consistente de fechas
 */

// Constantes para formatos de fecha
const DATE_FORMATS = {
    DISPLAY: 'DD/MM/YY HH:mm:ss',        // Formato mostrado al usuario
    BACKEND: 'YYYY-MM-DD HH:mm:ss',      // Formato para envío al backend (Y-m-d H:i:s)
    INTERNAL: 'YYYY-MM-DD HH:mm:ss',     // Formato interno de moment.js
    PICKER_DISPLAY: 'DD-MM-YYYY'        // Formato para el DatePicker
} as const;

/**
 * Convierte una fecha del formato interno al formato de display
 * @param dateString - Fecha en formato interno (YYYY-MM-DD HH:mm:ss)
 * @returns Fecha en formato display (DD/MM/YY HH:MM:SS)
 */
const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    const momentDate = moment(dateString, DATE_FORMATS.INTERNAL, true);
    return momentDate.isValid() ? momentDate.format(DATE_FORMATS.DISPLAY) : '';
};

/**
 * Convierte una fecha del formato interno al formato del backend
 * @param dateString - Fecha en formato interno (YYYY-MM-DD HH:mm:ss)
 * @returns Fecha en formato backend (Y-m-d H:i:s)
 */
const formatDateForBackend = (dateString: string): string => {
    if (!dateString) return '';
    const momentDate = moment(dateString, DATE_FORMATS.INTERNAL, true);
    return momentDate.isValid() ? momentDate.format(DATE_FORMATS.BACKEND) : '';
};

/**
 * Convierte una fecha de Date object a formato interno
 * @param date - Date object
 * @returns Fecha en formato interno (YYYY-MM-DD HH:mm:ss)
 */
const formatDateFromPicker = (date: Date | null): string => {
    if (!date) return '';
    return moment(date).format(DATE_FORMATS.INTERNAL);
};

/**
 * Convierte una fecha del formato picker al formato interno
 * @param dateString - Fecha en formato DD-MM-YYYY o YYYY-MM-DD HH:mm:ss
 * @returns Fecha en formato interno (YYYY-MM-DD HH:mm:ss)
 */
const parseDateFromPicker = (dateString: string): Date | null => {
    if (!dateString) return null;
    
    // Si ya está en formato interno, convertir directamente
    let momentDate = moment(dateString, DATE_FORMATS.INTERNAL, true);
    if (momentDate.isValid()) {
        return momentDate.toDate();
    }
    
    // Si está en formato picker, convertir
    momentDate = moment(dateString, DATE_FORMATS.PICKER_DISPLAY, true);
    return momentDate.isValid() ? momentDate.toDate() : null;
};

/**
 * Valida que una fecha esté en el formato correcto
 * @param dateString - Fecha a validar
 * @returns true si la fecha es válida
 */
const validateDateFormat = (dateString: string): boolean => {
    if (!dateString) return true; // Permitir fechas vacías si no son requeridas
    const momentDate = moment(dateString, DATE_FORMATS.INTERNAL, true);
    return momentDate.isValid();
};

interface CompletePickupModalProps {
    isOpen: boolean;
    onClose: () => void;
    pickup: Pickup;
}

interface ProductDelivery {
    id: string;
    type: string;
    quantity: number;
}

const validationSchema = yup.object({
    vehicleId: yup.string().required('El vehículo es requerido'),
    userId: yup.string().notRequired(),
    pickedWeightKg: yup
        .number()
        .positive('El peso debe ser mayor a 0')
        .required('El peso recogido es requerido'),
    comments: yup.string().notRequired(),
    scheduleNextDate: yup
        .string()
        .notRequired()
        .test('valid-format', 'El formato de fecha no es válido', (val) => {
            return validateDateFormat(val || '');
        }),
    products: yup
        .array()
        .of(
            yup.object({
                id: yup.string().required('El producto es requerido'),
                quantity: yup
                    .number()
                    .required('La cantidad es requerida')
                    .min(1, 'La cantidad debe ser al menos 1'),
            })
        )
        .min(1, 'Se requiere al menos un producto')
        .required('Se requiere al menos un producto'),
});

const CompletePickupModal: React.FC<CompletePickupModalProps> = ({
    isOpen,
    onClose,
    pickup,
}) => {

    // STATES

    const [loading, setLoading] = useState(false);
    const [isSearched, setIsSearched] = useState(false);

    // HOOKS

    const { getVehiclesList, vehiclesLoading } = useVehicles({ not_sold: true });
    const { getProductsList } = useProducts();
    const { getUsersList, usersLoading } = useUsers();
    const { userCan } = usePrivilege();
    const { user } = useSelector((state: RootState) => state.auth);
    const isAdmin = userCan('admin', 'pickup');
    const isDriver = userCan('driver', 'pickup') && !isAdmin;
    const [suggestedVehicle, setSuggestedVehicle] = useState<{ id: string; plateNumber: string; brand?: string; model?: string } | null>(null);
    const [showSuggestionModal, setShowSuggestionModal] = useState<boolean>(false);
    const [showConfirmUpdateVehicleModal, setShowConfirmUpdateVehicleModal] = useState<boolean>(false);
    const [pendingSubmitValues, setPendingSubmitValues] = useState<any | null>(null);
    const { getCompaniesList } = useCompanies();
    const { handleErrors } = useHandleErrors();

    // SERVICES

    const pickupService = new PickupService();

    // FORMIK

    const formik = useFormik({
        initialValues: {
            vehicleId: pickup.vehicle?.id || '',
            userId: isAdmin ? pickup.user?.id : (user?.id || ''),
            pickedWeightKg: pickup.pickedWeightKg || 0,
            comments: pickup.comments || '',
            scheduleNextDate: pickup?.scheduledNext?.date || '',
            company: pickup.company?.id || '',
            products: [],
        },
        validationSchema,
        onSubmit: async (values) => handleSubmit(values),
    });



    // Sugerencia de vehículo del día para el conductor (driver UI)
    useEffect(() => {
        const fetchSuggestedVehicle = async () => {
            if ( !formik.values.userId ) return;
            try {
                // asegurar que la lista de vehículos esté disponible
                const resp = await pickupService.getDriverFirstVehicleOfDay(formik.values.userId);
                const data = resp.getResponseData().data;
                if (data && data.id) {
                    setSuggestedVehicle({
                        id: data.id,
                        plateNumber: data.plateNumber,
                        brand: data.brand,
                        model: data.model,
                    });
                    // Mostrar popup para aceptar o rechazar el vehículo sugerido
                    if (!formik.values.vehicleId || formik.values.vehicleId !== data.id) {
                        setShowSuggestionModal(true);
                    }
                } else {
                    setSuggestedVehicle(null);
                }
            } catch (e) {
                // Silencioso: si falla la sugerencia, no bloquear el flujo
                setSuggestedVehicle(null);
            }
        };
        fetchSuggestedVehicle();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.userId, pickup.user?.id]);


    // FUNCTIONS

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EJECUTAR RECOGIDA
     * @EN EXECUTE PICKUP
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleSubmit = async (values: any) => {
        // Antes de enviar, si el vehículo seleccionado es distinto al sugerido, preguntar si desea actualizar el sugerido
        if (suggestedVehicle && values.vehicleId && suggestedVehicle.id !== values.vehicleId) {
            setPendingSubmitValues(values);
            setShowConfirmUpdateVehicleModal(true);
            return; // detener envío hasta recibir confirmación del usuario
        }

        setLoading(true);
        
        // Convertir fecha al formato del backend si existe
        const backendFormattedDate = values.scheduleNextDate ? formatDateForBackend(values.scheduleNextDate) : '';
        
        // Validar formato antes del envío
        if (values.scheduleNextDate && !validateDateFormat(values.scheduleNextDate)) {
            toast.error('El formato de fecha no es válido');
            setLoading(false);
            return;
        }

        const valuesToExecute = {
            id: pickup?.id,
            ...values,
            scheduleNextDate: backendFormattedDate, // Fecha en formato Y-m-d H:i:s para el backend
        };
        try {
            const response = await (await pickupService.executePickup(valuesToExecute)).getResponseData();
            if(response.success){
                toast.success('Recogida ejecutada con éxito');
            } else {
                handleErrors(response);
            }
            handleClose();
        } catch (error) {
            console.error('❌ [CompletePickup] Error:', error);
            toast.error('Error al ejecutar la recogida');
        } finally {
            setLoading(false);
        }
    };
    //-------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES PROCESAR ENVÍO DESPUÉS DE PREGUNTAR SOBRE ACTUALIZAR VEHÍCULO DAILY
     * @EN PROCESS SUBMIT AFTER ASKING ABOUT UPDATING DAILY VEHICLE
     */
    //-------------------------------------------------------------------------------------------------------------
    const proceedSubmitAfterVehiclePrompt = async (updateDaily: boolean) => {
        if (!pendingSubmitValues) {
            setShowConfirmUpdateVehicleModal(false);
            return;
        }
        setLoading(true);
        try {
            // Si el usuario acepta, actualizar el vehículo sugerido del día al seleccionado
            if (updateDaily) {
                const targetUserId = isAdmin ? formik.values.userId : (user?.id || null);
                if (targetUserId && pendingSubmitValues.vehicleId) {
                    await pickupService.updateDriverDailyVehicle(targetUserId, pendingSubmitValues.vehicleId);
                }
            }

            // Convertir fecha al formato del backend si existe
            const backendFormattedDate = pendingSubmitValues.scheduleNextDate ? 
                formatDateForBackend(pendingSubmitValues.scheduleNextDate) : '';

            const valuesToExecute = {
                id: pickup?.id,
                ...pendingSubmitValues,
                scheduleNextDate: backendFormattedDate, // Fecha en formato Y-m-d H:i:s para el backend
            };
            const response = await (await pickupService.executePickup(valuesToExecute)).getResponseData();
            if(response.success){
                toast.success('Recogida ejecutada con éxito');
            } else {
                handleErrors(response);
            }
            handleClose();
        } catch (error) {
            toast.error('Error al ejecutar la recogida');
        } finally {
            setLoading(false);
            setShowConfirmUpdateVehicleModal(false);
            setPendingSubmitValues(null);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES CERRAR MODAL
     * @EN CLOSE MODAL
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleClose = () => {
        formik.resetForm();
        onClose();
    };
    //-------------------------------------------------------------------------------------------------------------

    // USE EFFECT 

    useEffect(() => {
        if (!formik.values.company && getCompaniesList().length > 0 && !isSearched) {
            setIsSearched(true);
            let defaultCompany = getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU');
            formik.setFieldValue('company', defaultCompany?.value ?? '');
        }
    }, [getCompaniesList]);

    // RENDER

    return (
        <ModalForm
            isOpen={isOpen}
            onClose={handleClose}
            title={`Completar Recogida - ${pickup.headquarter?.name || 'Sin sede'}`}
        >
            {/* Popup de sugerencia de vehículo para conductor */}
            {suggestedVehicle && (
                <CustomModal
                    isOpen={showSuggestionModal}
                    onClose={() => setShowSuggestionModal(false)}
                    onConfirm={async () => {
                        // Aceptar sugerencia: seleccionar y actualizar vehículo del día
                        formik.setFieldValue('vehicleId', suggestedVehicle.id);
                        setShowSuggestionModal(false);
                        try {
                            await pickupService.updateDriverDailyVehicle(user?.id || null, suggestedVehicle.id);
                        } catch (error) {
                            // No bloquear si falla la actualización inmediata
                            console.warn('No se pudo actualizar el vehículo diario al aceptar la sugerencia');
                        }
                    }}
                    name={'Sugerencia de vehículo'}
                    confirmText={'Aceptar sugerencia'}
                    cancelText={'Rechazar'}
                    size={'sm'}
                >
                    <div className="mb-5 text-sm">
                        <p className="text-gray-700">
                            Se ha detectado un vehículo sugerido para hoy:
                        </p>
                        <p className="mt-2 font-medium">
                            {suggestedVehicle.plateNumber}
                            {suggestedVehicle.brand && suggestedVehicle.model ? ` (${suggestedVehicle.brand} ${suggestedVehicle.model})` : ''}
                        </p>
                        <p className="mt-2 text-gray-600">
                            ¿Deseas usar este vehículo por defecto para tus recogidas de hoy? Podrás cambiarlo manualmente si lo necesitas.
                        </p>
                    </div>
                </CustomModal>
            )}
            {/* Popup antes de enviar: actualizar vehículo sugerido si difiere del seleccionado */}
            {showConfirmUpdateVehicleModal && (
                <CustomModal
                    isOpen={showConfirmUpdateVehicleModal}
                    onClose={() => {
                        // Continuar sin actualizar el vehículo sugerido
                        proceedSubmitAfterVehiclePrompt(false);
                    }}
                    onConfirm={async () => {
                        await proceedSubmitAfterVehiclePrompt(true);
                    }}
                    name={'Actualizar vehículo sugerido'}
                    confirmText={'Sí, actualizar'}
                    cancelText={'No, continuar'}
                    size={'sm'}
                >
                    <div className="mb-5 text-sm">
                        <p className="text-gray-700">
                            El vehículo seleccionado es diferente al vehículo sugerido.
                        </p>
                        <p className="mt-2">
                            <span className="font-medium">Seleccionado:</span>{' '}
                            {(() => {
                                const selected = getVehiclesList().find((v: any) => v.value === (pendingSubmitValues?.vehicleId || formik.values.vehicleId));
                                return selected?.label || selected?.value || 'N/A';
                            })()}
                        </p>
                        {suggestedVehicle && (
                            <p className="mt-1">
                                <span className="font-medium">Sugerido:</span>{' '}
                                {suggestedVehicle.plateNumber}
                                {suggestedVehicle.brand && suggestedVehicle.model ? ` (${suggestedVehicle.brand} ${suggestedVehicle.model})` : ''}
                            </p>
                        )}
                        <p className="mt-3 text-gray-600">
                            ¿Deseas actualizar tu vehículo sugerido del día al que has seleccionado? Esto no afecta a la recogida actual.
                        </p>
                    </div>
                </CustomModal>
            )}
            
            <form onSubmit={formik.handleSubmit} className="space-y-6">
                {/* Información de la recogida */}
                <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-900 mb-2 font-bold">Información de la Recogida</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <span className="font-medium">Sede:</span> {pickup.headquarter?.name || 'N/A'}
                        </div>
                        <div>
                            <span className="font-medium">Estado:</span> {pickupStatuses.find(status => status.value === pickup?.pickupStatus)?.label || 'N/A'}
                        </div>
                        <div>
                            <span className="font-medium">Ruta:</span> {pickup.route?.name || 'N/A'}
                        </div>
                    </div>
                </div>

                {/* Campos del formulario */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Vehículo */}
                    <div>
                        <CustomSelect
                            id="vehicleId"
                            label="Vehículo"
                            placeholder="vehículo"
                            options={getVehiclesList()}
                            value={getVehiclesList().find(vehicle => vehicle.value === formik.values.vehicleId) || null}
                            onChange={(value: any) => formik.setFieldValue('vehicleId', value?.value || '')}
                            error={formik.errors.vehicleId}
                            isClearable={true}
                            isSearchable={true}
                            isLoading={vehiclesLoading}
                            required={true}
                        />
                    </div>

                    <div>
                        {isAdmin && (
                            <CustomSelect
                                id="userId"
                                label="Usuario"
                                placeholder="usuario"
                                options={getUsersList()}
                                value={getUsersList().find(u => u.value === formik.values.userId) || null}
                                onChange={(value: any) => formik.setFieldValue('userId', value?.value || '')}
                                error={formik.errors.userId}
                                isClearable={true}
                                isSearchable={true}
                                isLoading={usersLoading}
                                required={false}
                            />
                        )}
                    </div>

                    {/* Peso recogido */}
                    <div>
                        <RequiredInput
                            id="pickedWeightKg"
                            label="Peso recogido (kg)"
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="E.j. 10.50"
                            formik={formik}
                        />
                    </div>

                    <div>
                        <label htmlFor="scheduleNextDate" className="block text-sm font-medium text-gray-900 mb-2">
                            Programar próxima recogida
                        </label>
                        <DatePicker
                            monthsShown={1}
                            placeholderText="Selecciona una fecha y hora (DD/MM/YY HH:MM:SS)"
                            showTimeSelect={true}
                            timeIntervals={15}
                            timeFormat="HH:mm"
                            dateFormat="dd/MM/yyyy HH:mm"
                            inputProps={{
                                inputClassName: `h-9 [&_input]:text-ellipsis bg-white ${formik.errors.scheduleNextDate ? 'border-red-500' : ''}`,
                            }}
                            className="col-span-12"
                            selected={formik.values?.scheduleNextDate ? parseDateFromPicker(String(formik.values.scheduleNextDate)) : null}
                            onChange={(date: Date | null) => {
                                const internalFormat = formatDateFromPicker(date);
                                formik.setFieldValue('scheduleNextDate', internalFormat);
                            }}
                            locale={es}
                        />
                        {formik.errors.scheduleNextDate ? (
                            <span className="text-xs text-red-600">
                                {String(formik.errors.scheduleNextDate)}
                            </span>
                        ) : null}
                    </div>

                </div>

                {/* Comentarios */}
                <div>
                    <label htmlFor="comments" className="block text-sm font-medium text-gray-900 mb-2">
                        Comentarios (opcional)
                    </label>
                    <Textarea
                        id="comments"
                        placeholder="Agregar comentarios sobre la recogida..."
                        rows={3}
                        {...formik.getFieldProps('comments')}
                    />
                </div>

                {/* Selección de productos y cantidades */}
                <div className="space-y-4">
                    <CustomSelect
                        id="products"
                        label="Productos"
                        placeholder="Seleccionar productos"
                        options={getProductsList()}
                        value={
                            getProductsList().filter((option: any) =>
                                (formik.values.products as ProductDelivery[]).some((p) => p.id === option.value)
                            ) || null
                        }
                        onChange={(value: any) => {
                            const selectedIds: string[] = value?.map((v: any) => v.value) || [];
                            const current: ProductDelivery[] = (formik.values.products as ProductDelivery[]) || [];
                            const updated: ProductDelivery[] = selectedIds.map((id) => {
                                const existing = current.find((p) => p.id === id);
                                return existing ? existing : { id: id, quantity: 1, type: 'out' };
                            });
                            formik.setFieldValue('products', updated);
                        }}
                        error={formik.errors.products}
                        isClearable={true}
                        isSearchable={true}
                        isLoading={!getProductsList()}
                        required={true}
                        isMulti={true}
                    />

                    {(formik.values.products as ProductDelivery[]).length > 0 && (
                        <div className="space-y-3">
                            {(formik.values.products as ProductDelivery[]).map((p) => {
                                const option = getProductsList().find((o: any) => o.value === p.id);
                                return (
                                    <div key={p.id} className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                                        <div>
                                            <label className="text-sm font-medium text-gray-900">{option?.label || 'Producto'}</label>
                                        </div>
                                        <div>
                                            <RequiredInput
                                                id={`qty-${p.id}`}
                                                label="Cantidad"
                                                type="number"
                                                min="1"
                                                value={p.quantity}
                                                onChange={(e: any) => {
                                                    const qty = Number(e.target.value) || 1;
                                                    const updated = (formik.values.products as ProductDelivery[]).map((prod) =>
                                                        prod.id === p.id ? { ...prod, quantity: qty, type: 'out' } : prod
                                                    );
                                                    formik.setFieldValue('products', updated);
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {userCan('list', 'company') && (
                        <CustomSelect
                            required
                            isSearchable
                            id={'company'}
                            label="Compañia"
                            containerClassName='md:col-span-12'
                            value={
                                formik.values.company ?
                                { value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
                                getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU')
                            }
                            options={getCompaniesList()}
                            onChange={
                                (e: any) => { formik.setFieldValue('company', e?.value) }
                            }
                            error={formik.errors.company}
                        />
                    )}
                </div>

                {/* Botones */}
                <div className="flex justify-end gap-3 py-4 border-t">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        isLoading={loading}
                        disabled={
                            !formik.values.vehicleId ||
                            !formik.values.pickedWeightKg ||
                            !(formik.values.products as ProductDelivery[]).length ||
                            loading
                        }
                    >
                        Completar Recogida
                    </Button>
                </div>
            </form>
        </ModalForm>
    );
};

export default CompletePickupModal;